import { Button } from '@parcel/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { useAuth } from '../../lib/auth.js';
import { isSupabaseConfigured } from '../../lib/supabase.js';
import { getWorkspace } from '../../lib/workspaces.js';
import { ParcelLoader } from '../parcel-loader.js';

interface WorkspaceAccessGateProps {
  workspaceSlug: string;
  children: ReactNode;
  standalone?: boolean;
}

type WorkspaceAccessState = 'loading' | 'allowed' | 'error';
type WorkspaceAccessRecoveryAction =
  | { label: 'Switch workspace'; to: '/workspaces/select' }
  | { label: 'Sign out'; action: 'sign-out' };

export function canRenderWorkspaceAccess(input: {
  supabaseConfigured: boolean;
  state: WorkspaceAccessState;
  workspaceSlug: string;
  validatedSlug: string | null;
}): boolean {
  if (!input.supabaseConfigured) return true;
  return input.state === 'allowed' && input.validatedSlug === input.workspaceSlug;
}

export function getWorkspaceAccessErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : 'Unable to verify workspace access.';
  if (message.includes('404')) return 'Workspace not found or you do not have access.';
  if (message.includes('401')) return 'You do not have access to this workspace.';
  return message;
}

export function getWorkspaceAccessRecoveryActions(): WorkspaceAccessRecoveryAction[] {
  return [
    { label: 'Switch workspace', to: '/workspaces/select' },
    { label: 'Sign out', action: 'sign-out' },
  ];
}

export function WorkspaceAccessGate({
  workspaceSlug,
  children,
  standalone = false,
}: WorkspaceAccessGateProps) {
  const { signOut } = useAuth();
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug),
  });
  const state: WorkspaceAccessState = !isSupabaseConfigured
    ? 'allowed'
    : workspaceQuery.isPending
      ? 'loading'
      : workspaceQuery.isError
        ? 'error'
        : 'allowed';
  const error = workspaceQuery.isError
    ? getWorkspaceAccessErrorMessage(workspaceQuery.error)
    : null;
  const validatedSlug = !isSupabaseConfigured || workspaceQuery.isSuccess ? workspaceSlug : null;

  if (
    canRenderWorkspaceAccess({
      supabaseConfigured: isSupabaseConfigured,
      state,
      workspaceSlug,
      validatedSlug,
    })
  ) {
    return <>{children}</>;
  }

  const shellClassName = standalone
    ? 'grid min-h-svh place-items-center bg-muted p-6'
    : 'grid min-h-[calc(100svh-3.5rem)] place-items-center p-6';
  const recoveryActions = getWorkspaceAccessRecoveryActions();

  return (
    <main className={shellClassName}>
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        {state === 'loading' ? (
          <ParcelLoader label="Opening workspace" />
        ) : (
          <>
            <AlertCircle className="size-5 text-destructive" />
            <div className="space-y-1">
              <p className="font-medium text-sm">Could not open workspace</p>
              <p className="text-muted-foreground text-sm">{error}</p>
            </div>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              {recoveryActions.map((action) =>
                'to' in action ? (
                  <Button key={action.label} asChild variant="outline">
                    <Link to={action.to}>{action.label}</Link>
                  </Button>
                ) : (
                  <Button
                    key={action.label}
                    type="button"
                    variant="ghost"
                    onClick={() => void signOut()}
                  >
                    {action.label}
                  </Button>
                ),
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
